import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  MdAccountBalanceWallet,
  MdAttachMoney,
  MdChevronLeft,
  MdChevronRight,
  MdMoneyOff,
  MdPercent,
  MdReceiptLong,
  MdRemoveCircleOutline,
  MdSchedule,
  MdShowChart,
  MdTrendingUp,
  MdWarningAmber,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import BaseChart from '../../../Components/Analytics/BaseChart';
import AnalyticsChatWidget from '../../../Components/Analytics/AnalyticsChatWidget';
import BackButton from '../../../Components/Common/BackButton';
import CustomDatePicker from '../../../Components/Common/CustomDatePicker';
import PageTitle from '../../../Components/Common/PageTitle';
import DataGrid from '../../../Components/Table/DataGrid';
import { useAuth } from '../../../Context/AuthContext';
import { useChartTheme } from '../../../Context/ThemeContext';
import { getOrganizationRevenue } from '../../../Services/AnalyticsService';
import {
  revenueCollectionsTrendOption,
  revenuePayerAgingOption,
  revenuePaymentLagOption,
  revenueWaterfallOption,
} from '../../../Utils/AnalyticsOptions';
import {
  defaultRevenueRange,
  formatApiDate,
  formatCurrency,
  formatDays,
  formatPercent,
  revenueMetricColumns,
} from './revenueUtils';

const BREAKDOWN_TABS = [
  { id: 'payers', label: 'Payers', key: 'payer', heading: 'Payer' },
  { id: 'cpt_codes', label: 'Procedures (CPT)', key: 'cpt_code', heading: 'CPT Code' },
  { id: 'providers', label: 'Providers', key: 'provider_name', heading: 'Provider' },
  { id: 'facilities', label: 'Facilities', key: 'facility_name', heading: 'Facility' },
];

const WORKLIST_TABS = [
  { id: 'outstanding_worklist', countKey: 'outstanding_visits', label: 'Outstanding balances', hint: 'Billed visits where charges are not covered by payments and adjustments, largest balance first.' },
  { id: 'unbilled_worklist', countKey: 'unbilled_visits', label: 'Not yet billed', hint: 'Visits in the range with no billing record, oldest first.' },
];

const WORKLIST_COLUMNS = [
  { id: 'visit_number', label: 'Visit #', minWidth: 100 },
  { id: 'service_date', label: 'Service Date', minWidth: 110, sortAccessor: (row) => -(row.days_since_service ?? 0) },
  { id: 'days_since_service', label: 'Age', minWidth: 70, align: 'right', format: (_v, row) => formatDays(row.days_since_service) },
  { id: 'patient_name', label: 'Patient', minWidth: 180 },
  { id: 'payer', label: 'Payer', minWidth: 160 },
  { id: 'provider_name', label: 'Provider', minWidth: 140 },
  { id: 'cpt_code', label: 'CPT', minWidth: 80 },
  { id: 'charges', label: 'Charges', minWidth: 100, align: 'right', format: (_v, row) => formatCurrency(row.charges) },
  { id: 'collected', label: 'Collected', minWidth: 100, align: 'right', format: (_v, row) => formatCurrency(row.collected) },
  { id: 'adjustment', label: 'Adjustments', minWidth: 110, align: 'right', format: (_v, row) => formatCurrency(row.adjustment) },
  { id: 'balance', label: 'Balance', minWidth: 100, align: 'right', format: (_v, row) => <strong>{formatCurrency(row.balance)}</strong> },
];

const PillTabs = ({ tabs, active, onChange, counts }) => (
  <ul className="nav nav-pills gap-2 d-flex flex-column flex-md-row flex-wrap w-100 mb-3">
    {tabs.map((tab) => (
      <li className="nav-item" key={tab.id}>
        <button
          type="button"
          className={`nav-link border px-3 py-2 rounded-3 fw-semibold w-100 text-center ${active === tab.id ? 'hc-bg-primary text-white' : 'bg-light text-secondary'}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}{counts ? ` (${(counts[tab.id] || 0).toLocaleString()})` : ''}
        </button>
      </li>
    ))}
  </ul>
);

export default function AnalyticsRevenueView() {
  const chartTheme = useChartTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { organization_id: organizationId } = useParams();
  const { token } = useAuth();

  const [startDate, setStartDate] = React.useState(() => defaultRevenueRange().start);
  const [endDate, setEndDate] = React.useState(() => defaultRevenueRange().end);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [breakdownTab, setBreakdownTab] = React.useState(BREAKDOWN_TABS[0].id);
  const [worklistTab, setWorklistTab] = React.useState(WORKLIST_TABS[0].id);
  const [payerIndex, setPayerIndex] = React.useState(0);

  const loadRevenue = React.useCallback(async () => {
    if (!token || !organizationId || !startDate || !endDate) return;
    try {
      setLoading(true);
      setError(null);
      const response = await getOrganizationRevenue(token, organizationId, formatApiDate(startDate), formatApiDate(endDate));
      if (response?.is_success === false) throw new Error(response?.message || 'Failed to load organization revenue');
      setData(response?.data || null);
      setPayerIndex(0);
    } catch (err) {
      console.error('Error loading organization revenue:', err);
      setData(null);
      setError(err);
      toast.error('Unable to load organization revenue.');
    } finally {
      setLoading(false);
    }
  }, [token, organizationId, startDate, endDate]);

  React.useEffect(() => {
    loadRevenue();
  }, [loadRevenue]);

  const summary = React.useMemo(() => data?.summary || {}, [data]);
  const monthly = React.useMemo(() => data?.monthly || [], [data]);
  const paymentLag = React.useMemo(() => data?.payment_lag || [], [data]);
  const payerTrends = React.useMemo(() => data?.payer_trends || [], [data]);
  const activePayer = payerTrends[payerIndex] || null;
  const organizationName = data?.organization_name || location?.state?.organization_name || 'Organization';
  const activeBreakdown = BREAKDOWN_TABS.find((tab) => tab.id === breakdownTab);
  const activeWorklist = WORKLIST_TABS.find((tab) => tab.id === worklistTab);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trendOption = React.useMemo(() => revenueCollectionsTrendOption({ data: monthly }), [monthly, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const waterfallOption = React.useMemo(() => revenueWaterfallOption({ summary }), [summary, chartTheme]);
  const payerTrendOption = React.useMemo(() => revenueCollectionsTrendOption({
    data: activePayer?.months || [],
    title: activePayer ? activePayer.payer : 'Payer Trend',
    subtitle: activePayer ? `Payer ${payerIndex + 1} of ${payerTrends.length}, ranked by collections` : '',
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [activePayer, payerIndex, payerTrends.length, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const payerAgingOption = React.useMemo(() => revenuePayerAgingOption({ payerAging: data?.payer_aging }), [data, chartTheme]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const paymentLagOption = React.useMemo(() => revenuePaymentLagOption({ data: paymentLag }), [paymentLag, chartTheme]);

  const breakdownColumns = React.useMemo(() => [
    { id: activeBreakdown.key, label: activeBreakdown.heading, minWidth: 200 },
    ...revenueMetricColumns(),
  ], [activeBreakdown]);

  const breakdownCounts = React.useMemo(
    () => Object.fromEntries(BREAKDOWN_TABS.map((tab) => [tab.id, (data?.[tab.id] || []).length])),
    [data],
  );
  const worklistCounts = React.useMemo(
    () => Object.fromEntries(WORKLIST_TABS.map((tab) => [tab.id, summary[tab.countKey] || 0])),
    [summary],
  );

  const heroPrimary = [
    { label: 'Collected', value: formatCurrency(summary.collected), note: `${formatCurrency(summary.total_revenue)} incl. copay`, icon: <MdAttachMoney size={22} color="var(--hc-icon-amber-text)" /> },
    { label: 'Net Collection Rate', value: formatPercent(summary.net_collection_rate), note: 'Collected ÷ (charges − adjustments)', icon: <MdPercent size={22} color="var(--hc-status-success-text)" /> },
  ];
  const heroSnapshot = [
    { label: 'Outstanding Balance', value: formatCurrency(summary.outstanding), note: `${(summary.outstanding_visits || 0).toLocaleString()} visits`, icon: <MdAccountBalanceWallet size={22} color="var(--hc-status-danger-text)" /> },
    { label: 'Median Days to Payment', value: formatDays(summary.median_days_to_payment), note: 'Service date → deposit', icon: <MdSchedule size={22} color="var(--hc-icon-blue-alt)" /> },
  ];

  const metricCards = [
    { label: 'Billed Charges', value: formatCurrency(summary.charges), note: `${(summary.billed_visits || 0).toLocaleString()} billed visits`, icon: <MdReceiptLong size={20} className="hc-text-primary" /> },
    { label: 'Adjustments', value: formatCurrency(summary.adjustment), note: 'Contractual write-offs', icon: <MdRemoveCircleOutline size={20} className="hc-text-primary" /> },
    { label: 'Gross Collection', value: formatPercent(summary.gross_collection_rate), note: 'Collected ÷ billed charges', icon: <MdShowChart size={20} className="hc-text-primary" /> },
    { label: 'Collected / Visit', value: formatCurrency(summary.collected_per_visit), note: `Avg charge ${formatCurrency(summary.avg_charge_per_visit)}`, icon: <MdTrendingUp size={20} className="hc-text-primary" /> },
    { label: 'Visits Paid $0', value: (summary.zero_paid_visits || 0).toLocaleString(), note: `${formatCurrency(summary.zero_paid_charges)} billed`, icon: <MdMoneyOff size={20} className="hc-text-primary" /> },
    { label: 'Visits Not Billed', value: (summary.unbilled_visits || 0).toLocaleString(), note: 'No billing record yet', icon: <MdWarningAmber size={20} className="hc-text-primary" /> },
  ];

  const renderHeroStat = (stat) => (
    <div className="d-flex align-items-center gap-3 p-3 rounded-4 h-100" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.15)' }}>
      <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 44, height: 44, backgroundColor: 'var(--hc-surface)' }}>
        {stat.icon}
      </div>
      <div className="flex-grow-1 text-break">
        <p className="mb-1 small text-white">{stat.label}</p>
        {loading ? (
          <>
            <div className="skeleton-hero rounded-2 mb-1" style={{ height: 22, width: '75%' }} />
            <div className="skeleton-hero rounded-2" style={{ height: 12, width: '55%' }} />
          </>
        ) : (
          <>
            <p className="fw-bold mb-0 text-white">{stat.value}</p>
            <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.7)' }}>{stat.note}</p>
          </>
        )}
      </div>
    </div>
  );

  const stepPayer = (direction) => setPayerIndex((index) => (index + direction + payerTrends.length) % payerTrends.length);
  const payerCarouselActions = payerTrends.length > 1 ? (
    <>
      <button type="button" onClick={() => stepPayer(-1)} title="Previous payer" aria-label="Previous payer">
        <MdChevronLeft size={20} />
      </button>
      <button type="button" onClick={() => stepPayer(1)} title="Next payer" aria-label="Next payer">
        <MdChevronRight size={20} />
      </button>
    </>
  ) : null;

  const renderChart = ({ key, title, subtitle, option, noData, className, toolbarActions = null }) => (
    <div key={key} className={className}>
      <BaseChart title={title} subtitle={subtitle} option={option} loading={loading} error={error} noData={!loading && noData} height="380px" toolbarActions={toolbarActions} />
    </div>
  );

  return (
    <div className="container-fluid py-4">
      <PageTitle title="Revenue - Aegis 360" />
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        <BackButton onClick={() => navigate(-1)} />
        <AnalyticsChatWidget organizationId={organizationId} organizationName={organizationName} />
      </div>

      <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
        <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
        <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

        <div className="row g-4 align-items-center position-relative">
          <div className="col-12 col-lg-8">
            <div className="d-flex flex-wrap align-items-center gap-2 gap-md-3 mb-3">
              <span className="px-3 py-2 rounded-pill fw-semibold bg-success text-white" style={{ fontSize: '0.82rem' }}>Revenue Analytics</span>
              <span className="px-3 py-2 rounded-pill fw-semibold bg-info text-white" style={{ fontSize: '0.75rem' }}>
                {`${formatApiDate(startDate)} → ${formatApiDate(endDate)}`}
              </span>
            </div>
            <h2 className="fw-bold text-white mb-3 text-break" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>{organizationName}</h2>
            <div className="row g-3">
              {heroPrimary.map((stat) => (
                <div key={stat.label} className="col-12 col-md-6">{renderHeroStat(stat)}</div>
              ))}
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
              <div className="row g-3">
                {heroSnapshot.map((stat) => (
                  <div key={stat.label} className="col-12 col-md-6 col-lg-12">{renderHeroStat(stat)}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 p-lg-5 mb-4 hc-surface hc-card-primary">
        <div className="d-flex flex-column flex-xl-row flex-wrap justify-content-between align-items-start align-items-xl-center gap-3 mb-4">
          <div>
            <h5 className="fw-bold mb-1 d-flex align-items-center gap-2 hc-text-primary">
              <MdAttachMoney size={20} />
              Revenue Overview
            </h5>
            <p className="text-secondary small mb-0">By date of service. Adjustments are write-offs and are not counted as revenue.</p>
          </div>
          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">
            <CustomDatePicker id="revenue-start-date" value={startDate} onChange={(value) => setStartDate(value ? new Date(`${value}T00:00:00`) : null)} />
            <CustomDatePicker id="revenue-end-date" value={endDate} onChange={(value) => setEndDate(value ? new Date(`${value}T00:00:00`) : null)} />
            <button
              type="button"
              className="btn btn-sm bg-danger text-white fw-semibold rounded-3"
              style={{ height: '36px', minWidth: '80px' }}
              onClick={() => {
                const range = defaultRevenueRange();
                setStartDate(range.start);
                setEndDate(range.end);
              }}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="row g-3">
          {metricCards.map((card) => (
            <div key={card.label} className="col-12 col-sm-6 col-lg-4 col-xxl-2">
              <div className="hc-surface rounded-4 p-3 h-100 shadow-sm">
                {loading ? (
                  <>
                    <div className="skeleton rounded-2" style={{ height: 16, width: '60%', marginBottom: 12 }} />
                    <div className="skeleton rounded-2" style={{ height: 24, width: '80%', marginBottom: 8 }} />
                    <div className="skeleton rounded-2" style={{ height: 12, width: '50%' }} />
                  </>
                ) : (
                  <div className="d-flex align-items-start gap-3">
                    <div className="d-inline-flex align-items-center justify-content-center rounded-4 flex-shrink-0" style={{ width: 40, height: 40, backgroundColor: 'rgba(var(--hc-icon-navy-blue-rgb), 0.08)' }}>
                      {card.icon}
                    </div>
                    <div className="text-break">
                      <div className="text-secondary small text-uppercase fw-semibold">{card.label}</div>
                      <div className="fw-bold fs-5 mt-1">{card.value}</div>
                      <div className="text-secondary small">{card.note}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="row g-3 mb-4">
        {renderChart({ key: 'trend', className: 'col-12 col-xl-8', title: 'Charges vs. collections', subtitle: 'Monthly billed charges, collections, adjustments and net collection rate', option: trendOption, noData: !monthly.length })}
        {renderChart({ key: 'waterfall', className: 'col-12 col-xl-4', title: 'Where billed charges go', subtitle: 'Charges less write-offs and payments', option: waterfallOption, noData: !summary.charges })}
        {renderChart({ key: 'payer-trend', className: 'col-12 col-xl-8', title: activePayer?.payer || 'Payer trend', subtitle: 'Monthly charges vs. collections for one payer at a time', option: payerTrendOption, noData: !activePayer, toolbarActions: payerCarouselActions })}
        {renderChart({ key: 'payment-lag', className: 'col-12 col-xl-4', title: 'Time to payment', subtitle: 'Paid visits by days from service to deposit', option: paymentLagOption, noData: !paymentLag.some((row) => row.visits) })}
        {renderChart({ key: 'payer-aging', className: 'col-12', title: 'Outstanding balance by payer & age', subtitle: 'Payers carrying the largest unpaid balances, by days since service', option: payerAgingOption, noData: !data?.payer_aging?.payers?.length })}
      </div>

      <div className="p-4 p-lg-5 mb-4 hc-surface hc-card-primary">
        <h5 className="fw-bold mb-1 hc-text-primary">Revenue Breakdown</h5>
        <p className="text-secondary small mb-3">Sort any column to find the payers, procedures, providers or facilities that collect least, pay slowest or carry the largest balances.</p>
        <PillTabs tabs={BREAKDOWN_TABS} active={breakdownTab} onChange={setBreakdownTab} counts={loading ? null : breakdownCounts} />
        <DataGrid
          key={breakdownTab}
          columns={breakdownColumns}
          rows={data?.[breakdownTab] || []}
          loading={loading}
          defaultRowsPerPage={10}
          stickyHeader
          getRowId={(row) => row[activeBreakdown.key]}
        />
      </div>

      <div className="p-4 p-lg-5 hc-surface hc-card-primary">
        <h5 className="fw-bold mb-1 hc-text-primary">Follow-up Worklists</h5>
        <p className="text-secondary small mb-3">{activeWorklist.hint} Showing up to {data?.worklist_limit ?? 100} visits.</p>
        <PillTabs tabs={WORKLIST_TABS} active={worklistTab} onChange={setWorklistTab} counts={loading ? null : worklistCounts} />
        <DataGrid
          key={worklistTab}
          columns={WORKLIST_COLUMNS}
          rows={data?.[worklistTab] || []}
          loading={loading}
          defaultRowsPerPage={10}
          stickyHeader
          getRowId={(row, index) => `${row.visit_number}-${row.service_date}-${index}`}
        />
      </div>
    </div>
  );
}
